/** @import { BunFile, BunRequest, Serve } from 'bun' */
import { app_dir, base, dir, embed } from '#@sveltejs/adapter-bun';
import path from 'node:path';

/**
 * Embedded assets are imported by identity path; on-disk assets live under the
 * output directory next to this module.
 * @param {string} subdir
 * @param {string} filename
 * @returns {string}
 */
function resolve_file(subdir, filename) {
	return embed ? filename : path.resolve(dir, subdir, filename);
}

/**
 * @typedef {Serve.Routes<never, string>[string]} RouteHandler
 * @typedef {{ hash: string, mtime: number, br?: boolean, gz?: boolean }} AssetMeta
 */

const CONTENT_ENCODING = { br: 'br', gz: 'gzip' };

// the URL Standard's path percent-encode set, plus `%` and `\` so they stay literal
// eslint-disable-next-line no-control-regex -- control characters are part of the encode set
const ESCAPED_PATH_CHAR = /[\u0000-\u001f\u007f-\u{10ffff} "#<>?`{}%\\]/gu;

/**
 * WHATWG path serialization: the exact bytes user agents put on the wire, because
 * Bun matches route keys against the raw request pathname.
 * @param {string} pathname
 * @returns {string}
 */
function encode_pathname(pathname) {
	return pathname.replace(ESCAPED_PATH_CHAR, (char) => encodeURIComponent(char));
}

/**
 * Registers a path both as user agents send it and fully percent-encoded,
 * so clients that escape sub-delims still match.
 * @param {string} pathname
 * @returns {string[]}
 */
function route_paths(pathname) {
	const minimal = encode_pathname(pathname);
	const full = pathname.split('/').map(encodeURIComponent).join('/');
	return minimal === full ? [minimal] : [minimal, full];
}

/**
 * @param {string} url
 * @returns {string[]}
 */
function to_paths(url) {
	return route_paths(path.posix.join(base, url));
}

/**
 * @param {string} url
 * @returns {string[]}
 */
function to_directory_paths(url) {
	const directory = `${path.posix.join(base, url).replace(/\/$/, '')}/`;
	const paths = route_paths(directory);

	if (directory !== '/') {
		paths.push(...route_paths(directory.slice(0, -1)));
	}

	return paths;
}

/**
 * If-None-Match takes precedence over If-Modified-Since (RFC 9110 §13.1.3);
 * dates compare at whole-second precision because HTTP dates have none finer.
 * @param {Request} request
 * @param {string} etag
 * @param {number} mtime
 * @returns {boolean}
 */
function is_fresh(request, etag, mtime) {
	const header = request.headers.get('if-none-match');
	if (header !== null) {
		return header
			.split(',')
			.some((value) => ['*', etag].includes(value.trim().replace(/^W\//, '')));
	}

	const since = Date.parse(request.headers.get('if-modified-since') ?? '');
	return Number.isFinite(since) && Math.trunc(mtime / 1000) <= Math.trunc(since / 1000);
}

/**
 * @param {string | null} accept
 * @param {AssetMeta} meta
 * @returns {'br' | 'gz' | null}
 */
function negotiate(accept, meta) {
	if (accept === null || (!meta.br && !meta.gz)) return null;

	const accepted = new Set();
	for (const part of accept.split(',')) {
		const [name = '', ...params] = part.trim().toLowerCase().split(';');
		if (params.some((param) => /^q=0(\.0*)?$/.test(param.trim()))) continue;
		accepted.add(name.trim());
	}

	if (meta.br && (accepted.has('br') || accepted.has('*'))) return 'br';
	if (meta.gz && (accepted.has('gzip') || accepted.has('*'))) return 'gz';
	return null;
}

/**
 * @param {string[]} paths
 * @param {RouteHandler} route
 * @returns {Array<[string, RouteHandler]>}
 */
function route_entries(paths, route) {
	return paths.map((route_path) => [route_path, route]);
}

/**
 * Serves one file with its build-time validator and precompressed variants.
 * @param {string} file
 * @param {AssetMeta} meta
 * @param {Record<string, string>} [extra_headers]
 * @returns {RouteHandler}
 */
function file_route(file, meta, extra_headers = {}) {
	const content_type = Bun.file(file).type;
	const last_modified = new Date(meta.mtime).toUTCString();

	/** @param {BunRequest} request */
	const handler = (request) => {
		// Bun serializes Range itself for file bodies; ranges apply to the identity representation
		const encoding =
			request.headers.get('range') === null
				? negotiate(request.headers.get('accept-encoding'), meta)
				: null;
		const etag = encoding === null ? `"${meta.hash}"` : `"${meta.hash}-${encoding}"`;

		/** @type {Record<string, string>} */
		const response_headers = {
			'content-type': content_type,
			...extra_headers,
			etag,
			'last-modified': last_modified
		};
		if (meta.br || meta.gz) response_headers['vary'] = 'accept-encoding';

		if (is_fresh(request, etag, meta.mtime)) {
			return new Response(null, { status: 304, headers: response_headers });
		}

		let body_file = file;
		if (encoding !== null) {
			response_headers['content-encoding'] = CONTENT_ENCODING[encoding];
			body_file = `${file}.${encoding}`;
		}
		return new Response(Bun.file(body_file), { headers: response_headers });
	};

	return { GET: handler };
}

/**
 * @param {string} url
 * @param {string | undefined} filename
 * @param {AssetMeta} meta
 * @returns {Array<[string, RouteHandler]>}
 */
export function client_asset(url, filename = url, meta) {
	const immutable = url.startsWith(`${app_dir}/immutable/`);
	const route = file_route(
		resolve_file('client', filename),
		meta,
		immutable ? { 'cache-control': 'public,max-age=31536000,immutable' } : {}
	);

	const paths = to_paths(url);
	if (url.endsWith('/index.html') || url === 'index.html') {
		paths.push(...to_directory_paths(url.slice(0, -'index.html'.length)));
	} else if (url.endsWith('.html')) {
		// sirv also serves `page.html` at `/page`
		paths.push(...to_paths(url.slice(0, -'.html'.length)));
	}

	return route_entries(paths, route);
}

/**
 * @param {string} url
 * @param {string} [filename]
 * @returns {BunFile}
 */
export function server_asset(url, filename = url) {
	return Bun.file(resolve_file('client', filename));
}

/**
 * @param {string} url
 * @param {string | undefined} filename
 * @param {AssetMeta} meta
 * @returns {Array<[string, RouteHandler]>}
 */
export function prerendered_asset(url, filename = url, meta) {
	const route = file_route(resolve_file('prerendered', filename), meta);
	return route_entries(to_paths(url), route);
}

/**
 * @param {string} url
 * @param {string} filename
 * @param {AssetMeta} meta
 * @returns {Array<[string, RouteHandler]>}
 */
export function prerendered_page(url, filename, meta) {
	const route = file_route(resolve_file('prerendered', filename), meta);
	// path already contains base, no need to add it here
	const entries = route_entries(route_paths(url), route);

	const inverted = url.endsWith('/') ? url.slice(0, -1) : `${url}/`;
	if (inverted) {
		const canonical = encode_pathname(url);
		entries.push(
			...route_entries(route_paths(inverted), {
				GET: (req) => {
					const location = canonical + new URL(req.url).search;
					return new Response(null, { status: 308, headers: { location } });
				}
			})
		);
	}

	return entries;
}

/**
 * @param {string} url
 * @param {number} status
 * @param {string} location
 * @returns {Array<[string, RouteHandler]>}
 */
export function prerendered_redirect(url, status, location) {
	const route = { GET: new Response(null, { status, headers: { location } }) };
	// path already contains base, no need to add it here
	return route_entries(route_paths(url), route);
}
